import { yupResolver } from "@hookform/resolvers/yup";
import FormHubSelect from "components/Common/Form/FormHubSelect";
import FormInput from "components/Common/Form/FormInput";
import FormTimeRangePicker from "components/Common/Form/FormTimeRangePicker";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import {
  useCreateTimeSlot,
  useTimeSlotListType,
  useUpdateTimeSlot,
} from "hooks";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import * as yup from "yup";
import { Switch } from "antd";

const validateSchema = t => {
  const rules = {
    name: yup
      .string()
      .required(t("message.required", { title: t("form.name") })),
    code: yup
      .string()
      .required(t("message.required", { title: t("form.code") })),
    hub: yup
      .object()
      .required(t("message.required", { title: t("form.hub") }))
      .nullable(),
    type: yup
      .object()
      .required(t("message.required", { title: t("form.type") }))
      .nullable(),
    time_range: yup
      .array()
      .of(yup.string().required())
      .test({
        test: value => {
          return (
            value && value.length > 0 && value.some(item => item.trim() !== "")
          );
        },
        message: t("message.required", { title: t("form.time_range") }),
      }),
  };
  const schema = yup.object().shape(rules);
  return schema;
};

const FormTimeslotModal = ({
  isOpenModal,
  toggleModal,
  fetchTimeSlot,
  timeslotUpdate = undefined,
}) => {
  const { t } = useTranslation(["timeslot"]);
  const [isEvenShift, setIsEvenShift] = useState(false);

  const {
    refetch: fetchTimeSlotListType,
    isFetching: isLoadingTimeSlotListType,
    data: dataTimeSlotListType,
  } = useTimeSlotListType();

  const mainForm = useForm({
    resolver: yupResolver(validateSchema(t)),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      code: "",
      hub: undefined,
      type: undefined,
      time_range: undefined,
      even_shift: false,
    },
  });
  const {
    control,
    formState: { errors },
    trigger,
    getValues,
    reset,
  } = mainForm;

  useEffect(() => {
    if (timeslotUpdate) {
      reset({
        name: timeslotUpdate.name,
        code: timeslotUpdate.code,
        hub: {
          label: timeslotUpdate.hub?.name,
          value: timeslotUpdate.hub?.id,
        },
        type: {
          label:
            dataTimeSlotListType?.find(t => t?.value == timeslotUpdate.type)
              ?.label || "",
          value: timeslotUpdate.type,
        },
        time_range: [timeslotUpdate.from_time, timeslotUpdate.to_time],
      });
      setIsEvenShift(timeslotUpdate.is_even_shift ? true : false);
    }
  }, [timeslotUpdate, reset, t]);

  const onSuccessMutate = () => {
    onCloseModal();
    fetchTimeSlot();
  };

  const { mutate: createTimeSlot } = useCreateTimeSlot(onSuccessMutate);
  const { mutate: updateTimeSlot } = useUpdateTimeSlot(onSuccessMutate);

  const onSubmit = () => {
    trigger().then(result => {
      if (result) {
        const formValue = getValues();
        const data = {
          name: formValue.name,
          code: formValue.code,
          hub_id: formValue.hub.value,
          type: formValue.type.value,
          from_time: formValue.time_range[0],
          to_time: formValue.time_range[1],
          is_even_shift: isEvenShift ? 1 : 0,
        };
        if (timeslotUpdate) {
          updateTimeSlot({
            id: timeslotUpdate.id,
            data,
          });
        } else {
          createTimeSlot(data);
        }
      }
    });
  };

  const typeOptions = dataTimeSlotListType || [];

  const onCloseModal = () => {
    reset({});
    toggleModal(false);
  };

  const { setValue } = mainForm;
  // const [is_level_2_modal, setIsLevel2Modal] = useState(true);
  // const handleChangeIsLevel2Modal = value => {
  //   setIsLevel2Modal(value);
  //   setValue("hub", null);
  // };

  useEffect(() => {
    fetchTimeSlotListType();
  }, []);

  return (
    <Modal
      size="md"
      isOpen={isOpenModal}
      role="dialog"
      autoFocus={true}
      centered={true}
      className="exampleModal"
      toggle={onCloseModal}
    >
      <ModalHeader toggle={onCloseModal}>
        {timeslotUpdate ? t("title.update") : t("title.create")}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col xs={12} md={6}>
            <FormGroup>
              <Label htmlFor="name">
                {t("form.name")} <span className="text-danger">*</span>
              </Label>
              <FormInput
                isLabel={false}
                isRequired
                t={t}
                fieldName="name"
                type="text"
                mainForm={mainForm}
                title={t("placeholder.enter", {
                  title: t("form.name"),
                })}
              />
            </FormGroup>
          </Col>
          <Col xs={12} md={6}>
            <FormGroup>
              <Controller
                control={control}
                name="even_shift"
                render={({ field }) => (
                  <div style={{ marginTop: "35px" }}>
                    <Input
                      {...field}
                      checked={isEvenShift}
                      onChange={e => setIsEvenShift(e.target.checked)}
                      type="checkbox"
                      className="mx-2"
                    />
                    <Label htmlFor="even_shift">{t("form.even_shift")}</Label>
                  </div>
                )}
              />
            </FormGroup>
          </Col>
          {!timeslotUpdate && (
            <Col xs={12} md={12}>
              <FormGroup>
                <Label htmlFor="code">
                  {t("form.code")} <span className="text-danger">*</span>
                </Label>
                <FormInput
                  isLabel={false}
                  isRequired
                  t={t}
                  fieldName="code"
                  type="text"
                  mainForm={mainForm}
                  title={t("placeholder.enter", {
                    title: t("form.code"),
                  })}
                />
              </FormGroup>
            </Col>
          )}

          <Col xs={12} md={6}>
            <FormHubSelect
              isRequired
              mainForm={mainForm}
              fieldName="hub"
              t={t}
              title={t("form.hub")}
              addressLevelHub={3}
            />
          </Col>
          {/* <div
            className="col-12 col-md-6"
            style={{ display: "flex", alignItems: "center" }}
          >
            <Label
              className="d-flex align-items-center gap-1 mb-0"
              style={{ cursor: "pointer" }}
              htmlFor="is_level_2"
              onClick={() => handleChangeIsLevel2Modal(!is_level_2_modal)}
            >
              <Switch
                checked={is_level_2_modal}
                onChange={handleChangeIsLevel2Modal}
              />
              Địa chỉ 2 cấp
            </Label>
          </div> */}
          <Col xs={12} md={6}>
            <FormGroup>
              <Label htmlFor="type">
                {t("form.type")} <span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("placeholder.select", {
                      title: t("form.type"),
                    })}
                    options={typeOptions}
                    isSearchable
                    className="basic-single w-100"
                    classNamePrefix="select"
                  />
                )}
              />
              {errors.type && (
                <MessageFormValidate error={errors.type.message} />
              )}
            </FormGroup>
          </Col>
          <Col xs={12} md={6}>
            <FormGroup>
              <Label htmlFor="time_range">
                {t("form.time_range")} <span className="text-danger">*</span>
              </Label>
              <FormTimeRangePicker
                t={t}
                mainForm={mainForm}
                fieldName="time_range"
                isError={true}
                format={"HH:mm"}
                minuteStep={15}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row className="text-end">
          <Col>
            <Button
              size="xs"
              color="danger"
              onClick={onCloseModal}
              className="mx-3"
            >
              {t("button.cancel")}
            </Button>
            <Button
              size="xs"
              color="primary"
              style={{ backgroundColor: "#3C64B1" }}
              onClick={onSubmit}
            >
              {t("button.submit")}
            </Button>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default FormTimeslotModal;
